import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import { ColorPallet } from "../utils/colorPallet";
import { useAppDispatch } from "../app/hooks";
import { getProductSalesHistoryData } from "../features/salesHistory/salesHistorySlice";
import LoadingSpinner from "../components/common/Loading";
import SearchIcon from "../components/IconComp/searchIcon";

import AllTable from "../components/commontable/table";
import { ProductSalesHistoryHeader } from "../utils/constants";

const getCurrentDate = () => {
  const today = new Date();
  const year = today?.getFullYear();
  const month = String(today?.getMonth() + 1)?.padStart(2, "0");
  const day = String(today?.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const ProductSalesHistory = () => {
  const dispatch = useAppDispatch();
  const [dateFilterType, setDateFilterType] = useState("single");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState<string>(getCurrentDate()); // Set the current date by default
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  //pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [rowData, setRowData] = useState<any>([]);
  const [totalCount, setTotalCount] = useState(0);

  const pharmacyId = localStorage?.getItem("pharmacyId");

  const getProductSalesHistory = useCallback(async () => {
    const dto: any = {
      page: page + 1, // API pages are 1-indexed
      limit: rowsPerPage,
      start_date:
        dateFilterType === "single"
          ? selectedDate
          : fromDate
          ? fromDate
          : undefined,
      end_date: toDate ? toDate : undefined, // Assuming there's a selected date
      search: searchQuery ? searchQuery : undefined,
    };

    try {
      setIsLoading(true);
      const res = await dispatch(
        getProductSalesHistoryData({ pharmacy_id: pharmacyId, dto })
      );

      if (res.payload) {
        setRowData(res?.payload);
        setTotalCount(res?.payload?.pagination?.totalItems || 0); // Ensure number type
      }
    } catch (error) {
      console.error("Error fetching manufacturer data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    dispatch,
    pharmacyId,
    page,
    rowsPerPage,
    selectedDate,
    toDate,
    fromDate,
    searchQuery,
  ]);

  useEffect(() => {
    getProductSalesHistory();
  }, [getProductSalesHistory]);

  useEffect(() => {
    setSelectedDate(getCurrentDate()); // Update selectedDate whenever component re-renders
  }, []); // Empty dependency array to run this effect only once after the component mounts

  const formatDateTime = (dateTimeString: any) => {
    const dateTime = new Date(dateTimeString);
    const date = `${dateTime.getFullYear()}-${(dateTime.getMonth() + 1)
      ?.toString()
      ?.padStart(2, "0")}-${dateTime?.getDate()?.toString()?.padStart(2, "0")}`;
    return date;
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };
  const handleDateFilter = (filterType: string) => {
    setDateFilterType(filterType);
    if (filterType === "single") {
      setFromDate("");
      setToDate("");
      setSelectedDate(getCurrentDate());
    } else {
      setSelectedDate("");
    }
  };

  const tableRowData = rowData?.invoice?.map((item: any) => {
    return {
      "Product Name": item?.medicine_name,
      Dose: ((d?: string) => {
        const cleaned = (d ?? "").replace(/\bnull\b/gi, "").trim();
        return cleaned || "-";
      })(item?.medicine_dose),
      "Generic Name": item?.medicine_generic_name
        ? item?.medicine_generic_name
        : "-",
      "Sold Quantity": item?.totalQuantitySold ? item?.totalQuantitySold : "-",
    };
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing page size
  };

  return (
    <>
      <Box
        sx={{
          margin: 1,
          borderRadius: "5px",
          backgroundColor: ColorPallet?.bgcolor,
          minHeight: {
            xs: "auto",
            sm: "80vh",
            lg: "80vh",
          },
          my: "1.2%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            ml: 1,
          }}
        >
          <Box sx={{ p: 1 }}>
            <RadioGroup
              row
              aria-label="date-filter-type"
              name="date-filter-type"
              value={dateFilterType}
              onChange={(e) => handleDateFilter(e?.target?.value)}
            >
              <FormControlLabel
                value="single"
                control={<Radio />}
                label="Select Date"
              />
              <FormControlLabel
                value="range"
                control={<Radio />}
                label="Select Date Range"
              />
            </RadioGroup>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "start", sm: "center" },
            gap: 2,
            ml: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            {dateFilterType === "single" && (
              <Box sx={{ p: 0, pl: 0 }}>
                <TextField
                  type="date"
                  size="small"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e?.target?.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ width: "227px" }} // Adjust the width here as needed
                />
              </Box>
            )}

            {dateFilterType === "range" && (
              <Box
                sx={{
                  p: 0,
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                }}
              >
                <TextField
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={(e) => setFromDate(e?.target?.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ height: "36px", width: "200px" }}
                />
                <TextField
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={(e) => setToDate(e?.target?.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ height: "36px", width: "200px" }}
                />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              gap: 2,
              mr: 2,
              p: 0,
            }}
          >
            {
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: "200px" }}
              />
            }
          </Box>
        </Box>

        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "45vh",
            }}
          >
            <LoadingSpinner />
          </Box>
        ) : tableRowData && tableRowData.length > 0 ? (
          <Box sx={{ mt: 1, p: 2, width: "100%" }}>
            <AllTable
              headerData={ProductSalesHistoryHeader}
              rowData={tableRowData}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              //hasAccess={hasAccess}
            />
          </Box>
        ) : (
          <Typography
            variant="body1"
            sx={{
              mt: 16,
              ml: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              // color: "red",
              fontSize: "14px",
            }}
          >
            {dateFilterType === "range" && (!fromDate || !toDate)
              ? "Please select date first."
              : "No data found."}
          </Typography>
        )}
      </Box>
      {/* )} */}
    </>
  );
};
export default ProductSalesHistory;
